// Types shared between the frontend and Netlify Functions.

export const ROLE_HIERARCHY = ["owner", "reviewer", "inspector", "resident_engineer"] as const;
export type Role = (typeof ROLE_HIERARCHY)[number];

/**
 * Priority order when sources conflict. Lower index = higher authority.
 * Enforced both as a retrieval tie-break and spelled out in the synthesis
 * system prompt so the model defers to it explicitly rather than blending
 * sources of different authority as if they were equal.
 */
export const SOURCE_TYPE_HIERARCHY = [
  "contract_plans",
  "special_provisions",
  "construction_control_directive",
  "standard_specification",
  "design_standard",
  "materials_testing",
  "veteran_knowledge",
  "other",
] as const;

export type SourceType = (typeof SOURCE_TYPE_HIERARCHY)[number];

export function sourceTypeRank(t: SourceType): number {
  const idx = SOURCE_TYPE_HIERARCHY.indexOf(t);
  return idx === -1 ? SOURCE_TYPE_HIERARCHY.length : idx;
}

export type DocumentStatus = "draft" | "approved" | "archived";
export type IngestStatus = "queued" | "processing" | "ready" | "failed";

export interface Project {
  id: string;
  name: string;
  contractNumber: string | null;
  createdAt: string;
}

/**
 * Sub-categorizes documents beyond the broad SourceType, so /ask can scope
 * retrieval to (and the frontend can prompt for) a specific standard
 * family. Computed by a generated column (db/migrations/010_standard_categories.sql)
 * from source_type + title — design_standard splits into roadway (titles
 * starting "R-") vs. a catch-all; every other source_type maps 1:1.
 */
export const STANDARD_CATEGORY_HIERARCHY = [
  "roadway_design_standard",
  "design_standard",
  "material_standard",
  "standard_specification",
  "special_provisions",
  "construction_control_directive",
  "contract_plans",
  "veteran_knowledge",
  "other",
] as const;

export type StandardCategory = (typeof STANDARD_CATEGORY_HIERARCHY)[number];

export const STANDARD_CATEGORY_LABEL: Record<StandardCategory, string> = {
  roadway_design_standard: "Roadway Design Standard",
  design_standard: "Design Standard",
  material_standard: "Material Standard",
  standard_specification: "Standard Specification",
  special_provisions: "Special Provisions",
  construction_control_directive: "Construction Control Directive",
  contract_plans: "Contract Plans",
  veteran_knowledge: "Veteran Knowledge",
  other: "Other",
};

export interface StandardCategoryOption {
  category: StandardCategory;
  label: string;
  documentCount: number;
}

export interface DocumentPageCoverage {
  inventoriedPages: number;
  ocrSucceededPages: number;
  structuredPages: number;
  unstructuredPages: number;
}

export interface DocumentRecord {
  id: string;
  projectId: string | null; // null = shared/public registry (e.g. ODOT statewide specs)
  title: string;
  sourceType: SourceType;
  standardCategory: StandardCategory;
  status: DocumentStatus;
  ingestStatus: IngestStatus;
  version: number;
  checksum: string;
  blobKey: string;
  originalFilename: string;
  mimeType: string;
  pageCount: number | null;
  uploadedBy: string;
  uploadedAt: string;
  approvedBy: string | null;
  approvedAt: string | null;
  ingestError: string | null;
  /** 0 until the reviewed table corrections have been re-applied to this document; 1 once "Repair reviewed tables" has run. The approval gate in db/migrations/084 refuses to approve a replacement while this is 0 and the document it supersedes carries reviewed corrections. */
  reviewedExtractionRevision?: number;
  /** Count of this document's document_tables rows with extraction_source='agent_drafted' AND review_status='pending_review' — see db/migrations/063_agent_drafted_table_review.sql. Only populated by endpoints that join it in (currently projects-sources.ts); absent elsewhere means "not computed for this call," not "zero." */
  pendingTableReviewCount?: number;
  /** Page inventory and interpretation counts from document_page_coverage, not the number of plan_sheets (which includes placeholders). */
  pageCoverage?: DocumentPageCoverage;
}

export interface ChunkRecord {
  id: string;
  documentId: string;
  page: number | null;
  section: string | null;
  content: string;
}

export type SheetType =
  | "title_index"
  | "typical_section"
  | "general_notes"
  | "pay_quantities"
  | "plan_profile"
  | "cross_section"
  | "drainage"
  | "survey_control"
  | "traffic_control"
  | "erosion_control"
  | "signing_striping"
  | "signals_lighting"
  | "utilities"
  | "bridge_structure"
  | "details"
  | "unclassified"
  | "other";

/** A single called-out feature on a drawing sheet, as read by the vision extraction pass — see netlify/functions/_shared/visionExtraction.ts. Station/offset are kept as the raw printed token (e.g. "1029+00", "85.00' LT") rather than parsed, since they're for display/citation, not range queries. */
export interface SheetFeatureNote {
  description: string;
  station: string | null;
  offset: string | null;
}

/** Structured read of one classified contract-plan sheet. */
export interface PlanSheetExtraction {
  sheetNumber: string | null;
  sheetType: SheetType;
  title: string | null;
  stationStart: string | null;
  stationEnd: string | null;
  typicalSectionRef: string | null;
  benchmarks: SheetFeatureNote[];
  drainageFeatures: SheetFeatureNote[];
  utilities: SheetFeatureNote[];
  rowImpacts: SheetFeatureNote[];
  payItemHints: string[];
}

export interface PlanSheetRecord {
  id: string;
  documentId: string;
  page: number;
  sheetNumber: string | null;
  sheetType: SheetType;
  title: string | null;
  stationStartFt: number | null;
  stationEndFt: number | null;
  typicalSectionRef: string | null;
  extracted: PlanSheetExtraction | null;
  visionModel: string | null;
}

export type ImpactFlag = "yes" | "possible" | "no" | "unknown";

export interface ImpactAssessment {
  flag: ImpactFlag;
  note: string;
}

export interface AnswerCitation {
  // The 1-based excerpt number the model was shown this source as — lets a
  // plan step's citationIndices point unambiguously at one of these cards.
  citationIndex: number;
  chunkId: string;
  documentId: string;
  documentTitle: string;
  // The cited document's version at the time it was retrieved — part of
  // "governing source and revision" for a bedding answer (shared/bedding.ts),
  // but populated on every citation, not just bedding ones.
  documentVersion: number;
  sourceType: SourceType;
  mimeType: string;
  page: number | null;
  section: string | null;
  excerpt: string;
}

export interface AnswerImpacts {
  changeOrder: ImpactAssessment;
  timeImpact: ImpactAssessment;
  payItem: ImpactAssessment;
  materials: ImpactAssessment;
  environmental: ImpactAssessment;
}

export type Confidence = "high" | "medium" | "low" | "insufficient_evidence";

/**
 * One step of a "plan"-kind AskResponse — see AskResponse.kind. citationIndices
 * point into the same numbered excerpt list citations are built from, so a
 * step can be grounded in more than one source (e.g. a rock-excavation
 * classification step citing both the standard spec section and a CCD).
 */
export interface PlanStep {
  order: number;
  action: string;
  rationale: string;
  citationIndices: number[];
}

// The distinct drainage-summary table shapes a real ODOT plan set carries
// (see shared/tableExtraction.ts) — a bare "drainage records" question
// doesn't say which one, so AskResponse can come back asking instead of
// guessing (kind: "clarify" below).
export type DrainageTableKind = "drainage_structures" | "drainage_design_record";

// ---------------------------------------------------------------------------
// Pipe bedding — response-shape types only. The pure resolution logic
// (parsing, matching, conflict/precedence handling) lives in shared/bedding.ts
// and imports these; kept here (not there) because BeddingResolution is part
// of the wire-level AskResponse the frontend also reads.
// ---------------------------------------------------------------------------

/** One parsed Standard Drawing reference out of a bedding-table cell. */
export interface DrawingReference {
  raw: string; // exact original token — never mutated, used for citation display
  normalized: string; // uppercased/whitespace-collapsed, for matching
  detail: string | null; // parsed trailing suffix, e.g. "A" from "RD-315-A" or "RD-315/A"
}

/** An outranked-but-real alternative disclosed alongside a resolved bedding answer. */
export interface ConflictItem {
  documentId: string;
  documentTitle: string;
  documentVersion: number;
  section: string | null;
  beddingClass: string;
  material: string;
  outrankedBy: string; // human-readable precedence explanation
}

/**
 * The deterministic facts behind a resolved bedding answer — populated only
 * from parsed pipe-bedding class table rows, never by the model (see
 * shared/bedding.ts's resolveBeddingAnswer). "governing source and revision"
 * is governingDocumentTitle/governingDocumentVersion; `conflicts` discloses
 * any outranked sources rather than silently blending them.
 */
export interface BeddingResolution {
  pipeType: string;
  beddingClass: string;
  material: string;
  pipeSizeRange: string | null;
  installationCondition: string | null;
  drawingRefs: DrawingReference[];
  tableNumber: string | null;
  notes: string | null;
  projectSpecificModification: string | null;
  governingDocumentId: string;
  governingDocumentTitle: string;
  governingDocumentVersion: number;
  governingSection: string | null;
  governingPage: number | null;
  governingChunkId: string;
  governingReason: string;
  conflicts: ConflictItem[];
  /** Auditable structured-table facts and the exact governing rule row. */
  decisionPath?: { dimension: string; value: string | number | boolean; source: "question" | "conversation" | "selection" }[];
  governingRuleId?: string;
}

/**
 * One option in a "clarify" AskResponse — see AskResponse.kind. Exactly one
 * hint field is set per option: `drainageKindHint` for the closed
 * drainage-table-kind enum, `pipeTypeHint` for bedding questions (an
 * open-ended string pulled from the project's actual pipe-bedding class
 * table — see shared/bedding.ts — never a hardcoded list). Re-selecting
 * sends whichever hint is present back to /ask.
 */
export interface ClarifyOption {
  label: string;
  drainageKindHint?: DrainageTableKind;
  pipeTypeHint?: string;
}

export interface AskResponse {
  auditId: string;
  // "directive" (default) is today's single terse answer. "plan" is for
  // correspondence describing a field situation that needs multi-step
  // diagnosis (e.g. a contractor email) — see shared/promptHierarchy.ts.
  // "clarify" is a question back to the inspector instead of an answer —
  // e.g. a bare "drainage records" question when the project has more than
  // one distinct drainage summary table and nothing in the question says
  // which — see projects-ask.ts. `directive` stays populated in every case
  // (the real directive, a synthesized plan summary, or the clarifying
  // question itself) so existing history/cache code that only ever reads
  // `.directive` keeps working unchanged.
  kind: "directive" | "plan" | "clarify";
  directive: string;
  steps?: PlanStep[];
  // Present only for kind: "clarify" — the choices to show instead of an
  // answer. Selecting one re-asks the same question with that hint set.
  clarifyOptions?: ClarifyOption[];
  /** Optional free-text value for an open-ended, table-driven clarification. */
  clarifyTextInput?: {
    label: string;
    placeholder: string;
    hint: "pipe_type";
  };
  // Optional guidance shown after the clarify choices. Bedding uses this to
  // tell the inspector exactly what the resolved, cited answer will contain
  // without burying the selectable pipe types beneath that detail.
  clarifyFollowUp?: string;
  // Present only when a bedding question resolved to a specific pipe type —
  // see shared/bedding.ts. Deterministic, never model-authored, so the UI
  // can show pipe type/class/material/drawing/governing source+revision as
  // hard facts even though `directive` itself is LLM prose grounded in the
  // same evidence.
  beddingResolution?: BeddingResolution;
  // Present only when the request included one or more attachments — what
  // was read from each (vision transcription for an image, extracted text
  // for a PDF/DOCX), shown back to the inspector so they can sanity-check it
  // before trusting an answer built from it.
  attachmentTranscriptions?: { filename: string | null; mimeType: string; transcription: string }[];
  confidence: Confidence;
  citations: AnswerCitation[];
  impacts: AnswerImpacts;
  conflictNote: string | null;
  verifyNotice: string;
}

/** One prior turn in the same session, sent back for follow-up questions. */
export interface ConversationTurn {
  question: string;
  directive: string;
}

/**
 * One full conversation, as read back from server-side chat history
 * (GET /api/projects/:id/ask-history) — same shape src/lib/answerCache.ts's
 * CachedThread already uses (turns oldest-first, cachedAt = last turn's
 * timestamp) so Rail.tsx's rendering needs no changes when its data source
 * moves from a local cache to this.
 */
export interface ChatThread {
  threadId: string;
  turns: { question: string; response: AskResponse }[];
  cachedAt: string;
}

export type FeedbackVerdict = "correct" | "incorrect";

export interface FeedbackRecord {
  auditId: string;
  verdict: FeedbackVerdict;
  correction: string | null;
  submittedBy: string;
  submittedAt: string;
  promotionRequested: boolean;
  promotionStatus: "none" | "pending" | "approved" | "rejected";
}

export interface DashboardStats {
  approvedSources: number;
  totalSources: number;
  draftSources: number;
  questionsThisWeek: number;
  highConfidenceRatePct: number | null; // null when there's no volume yet to rate
  openCorrections: number;
  openEscalations: number;
}

export type EscalationStatus = "open" | "resolved";

export interface EscalationResponse {
  id: string;
  responderEmail: string;
  responseText: string;
  createdAt: string;
}

/**
 * "Ask your team" — a question for other people on the same project to
 * weigh in on. Two ways in: escalating an existing AI answer (the AI
 * couldn't answer, or an inspector marked it wrong — auditId set,
 * directive carried over from that answer), or asking a colleague
 * directly with no AI answer involved at all (auditId/directive null).
 * Peer-to-peer, no owner gate on responding — deliberately separate from
 * FeedbackRecord/promotions, which is "was this right, maybe promote to
 * veteran knowledge" and stays owner-gated. Visible to exactly the
 * project's membership, same audience every other project-scoped endpoint
 * already resolves via requireProjectAccess.
 */
export interface Escalation {
  id: string;
  auditId: string | null;
  question: string;
  directive: string | null;
  note: string | null;
  status: EscalationStatus;
  raisedByEmail: string;
  createdAt: string;
  resolvedAt: string | null;
  responses: EscalationResponse[];
}

export interface DashboardNeedsAttentionItem {
  documentId: string;
  title: string;
  sourceType: SourceType;
  uploadedAt: string;
  /** "document_approval": the document itself is awaiting owner approval. "table_review": the document is already approved, but one or more agent-drafted tables inside it are still pending_review — see pendingTableCount. */
  reason: "document_approval" | "table_review";
  pendingTableCount: number | null;
}

export interface DashboardActivityItem {
  kind: "question" | "approval";
  text: string;
  confidence: Confidence | null;
  by: string;
  at: string;
}

export interface DashboardData {
  stats: DashboardStats;
  needsAttention: DashboardNeedsAttentionItem[];
  recentActivity: DashboardActivityItem[];
}

// ---------------------------------------------------------------------------
// Network: org-wide directory + connections between app_users. Not scoped to
// project_members — this is a separate, org-wide social graph.
// ---------------------------------------------------------------------------

export type ConnectionStatus = "pending" | "accepted" | "declined";

/** How the current viewer relates to another user — derived, not stored. */
export type ConnectionRelation = "none" | "pending_sent" | "pending_received" | "connected";

export interface UserProfile {
  userId: string;
  email: string;
  role: Role;
  fullName: string | null;
  title: string | null;
  bio: string | null;
  certifications: string[];
  avatarUrl: string | null;
  updatedAt: string | null;
}

/** Directory/search row — always the minimal public fields, never bio/certifications. */
export interface PersonSummary {
  id: string;
  email: string;
  role: Role;
  fullName: string | null;
  title: string | null;
  relation: ConnectionRelation;
}

/** Owner-only admin-users.ts row — the full account-management view, not the public directory row above. */
export interface AdminUserSummary {
  id: string;
  email: string;
  role: Role;
  fullName: string | null;
  createdAt: string;
  disabledAt: string | null;
  approvedAt: string | null;
  // Only meaningful for inspector/resident_engineer — owner/reviewer get
  // every project regardless of project_members (see auth.ts's
  // requireProjectAccess).
  projectIds: string[];
}

export interface ConnectionRecord {
  id: string;
  requesterId: string;
  requesterEmail: string;
  recipientId: string;
  recipientEmail: string;
  status: ConnectionStatus;
  createdAt: string;
  respondedAt: string | null;
}

// ---------------------------------------------------------------------------
// Direct messages — 1:1 only, gated by an accepted connection. connectionId
// doubles as the thread id (see db/migrations/009_messages.sql).
// ---------------------------------------------------------------------------

export interface MessageRecord {
  id: string;
  connectionId: string;
  senderId: string;
  senderEmail: string;
  recipientId: string;
  recipientEmail: string;
  body: string;
  createdAt: string;
  readAt: string | null;
}

/** One row in the Messages inbox — one per accepted connection. */
export interface MessageThreadSummary {
  otherUserId: string;
  otherUserEmail: string;
  otherUserFullName: string | null;
  lastMessage: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
}
